/** /preflight-groups — system-wide preflight checklist groups (grid + CRUD dialog). */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { groups, type PreflightGroup } from '@/api/preflight';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { PreflightGroupDialog } from './PreflightGroupDialog';

export function PreflightGroupsListPage() {
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const canManage = hasPerm('console_preflight_manage');

  const [editing, setEditing] = useState<PreflightGroup | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState<PreflightGroup | null>(null);

  useCreateIntent(() => openCreate());

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = useCallback((group: PreflightGroup) => {
    setEditing(group);
    setDialogOpen(true);
  }, []);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => groups.remove(id),
    onSuccess: () => {
      snackbar.show('Group deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['preflight', 'groups'] });
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const columns = useMemo<GridColDef<PreflightGroup>[]>(
    () => [
      copyableColumn({ field: 'code', headerName: 'Code', width: 180 }),
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
      { field: 'description', headerName: 'Description', flex: 2, minWidth: 240, sortable: false },
      { field: 'sequence', headerName: 'Seq', width: 90 },
      {
        field: 'item_count',
        headerName: 'Items',
        width: 100,
        sortable: false,
        valueGetter: (_, row) => row.item_count,
      },
      ...(canManage
        ? [
            {
              field: 'actions',
              headerName: '',
              width: 96,
              sortable: false,
              filterable: false,
              renderCell: (p: { row: PreflightGroup }) => (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(p.row);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setToDelete(p.row);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ),
            } as GridColDef<PreflightGroup>,
          ]
        : []),
    ],
    [canManage, openEdit],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      groups.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'sequence',
        search: params.search,
      }),
    [],
  );

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="preflight-concept"
        title="Checklist Groups"
        subtitle="System-wide groupings for preflight onboarding checklist items."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New group
            </Button>
          )
        }
      />

      <PreflightGroupDialog
        open={dialogOpen}
        group={editing}
        onClose={() => setDialogOpen(false)}
      />

      <ResourceDataGrid<PreflightGroup>
        queryKey={['preflight', 'groups']}
        queryFn={queryFn}
        columns={columns}
        defaultSort={{ field: 'sequence', sort: 'asc' }}
        emptyMessage="No checklist groups yet."
        emptyAction={
          canManage ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New group
            </Button>
          ) : undefined
        }
        headerToolbar={{
          search: true,
          searchPlaceholder: 'Code or name',
          exportFileName: 'preflight-groups',
        }}
      />

      <ConfirmDialog
        open={toDelete !== null}
        title="Delete this group?"
        message={
          toDelete
            ? `"${toDelete.name}" will be deleted. Templates referencing it will be left ungrouped. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete group"
        destructive
        onConfirm={() => {
          if (toDelete) deleteMutation.mutate(toDelete.id);
          setToDelete(null);
        }}
        onCancel={() => setToDelete(null)}
      />
    </Box>
  );
}
